/* eslint-disable @typescript-eslint/no-explicit-any */
import type { FormInstance, TableColumnType } from 'antd'
import { Typography } from 'antd'
import { isPlainObject, merge } from 'lodash-es'
import { useRef } from 'react'
import type { InfiniteListProps, InfiniteListRef } from 'react-toolkits'
import { InfiniteList } from 'react-toolkits'
import { formSchema } from '../schemas'
import { infiniteListSchema } from '../schemas/infinite-list'
import { renderSchemaArray, replaceValueWithJMESPath } from '../utils'
import { withParsedData } from './base'
import { FormRenderer } from './form'

interface ListData {
  rows: any[]
  rowKey: string
  hasMore: boolean
}

export const InfiniteListRenderer = withParsedData(infiniteListSchema)(({ parsedData }) => {
  const { storeId, columns, url, footer, actions, fields, gutter, initialValues, formLayout, params, extra, ...rest } =
    parsedData
  const ref = useRef<InfiniteListRef<any, any, ListData>>(null)

  const newColumns: (TableColumnType<any> & { tpl?: string })[] = columns ? [...columns] : []

  // 处理对象字面量或 JSON 字符串
  newColumns.forEach(column => {
    column.render = (value: any) => {
      if (isPlainObject(value)) {
        return (
          <Typography.Paragraph style={{ maxHeight: 200, overflow: 'auto' }}>
            <pre>{JSON.stringify(value, null, 2)}</pre>
          </Typography.Paragraph>
        )
      } else if (typeof value === 'string') {
        try {
          const obj = JSON.parse(value)
          if (isPlainObject(obj)) {
            return (
              <Typography.Paragraph style={{ maxHeight: 200, overflow: 'auto' }}>
                <pre>{JSON.stringify(obj, null, 2)}</pre>
              </Typography.Paragraph>
            )
          }
          return value
        } catch (error) {
          return value
        }
      } else {
        return value
      }
    }
  })

  if (actions) {
    newColumns.push({
      title: '操作',
      align: 'center',
      render: (record: any) => renderSchemaArray(actions, record),
    })
  }

  const renderForm = (form: FormInstance) => {
    const data = formSchema.parse({
      type: 'form',
      fields,
      initialValues,
      layout: formLayout,
      gutter,
    })
    return <FormRenderer data={data} form={form} externalStoreId={storeId} />
  }

  const renderFooter = footer && (() => renderSchemaArray(footer, ref.current?.data))

  const getRowKey: InfiniteListProps<any, any, ListData>['getRowKey'] = data => data?.rowKey
  const getDataSource: InfiniteListProps<any, any, ListData>['getDataSource'] = data =>
    data?.reduce((acc, curr) => acc.concat(curr?.rows ?? []), [] as any[]) ?? []

  const getHasMore: InfiniteListProps<any, any, ListData>['getHasMore'] = data => !!data?.hasMore

  const getParams: InfiniteListProps<any, any, ListData>['params'] = (values, rowKey) => {
    if (params) {
      return merge(
        {
          rowKey: rowKey,
        },
        replaceValueWithJMESPath(values, params),
      )
    } else {
      return {
        ...values,
        rowKey,
      }
    }
  }

  const tableExtra = renderSchemaArray(extra)

  return (
    <InfiniteList
      {...rest}
      ref={ref}
      url={url}
      columns={newColumns}
      footer={renderFooter}
      getRowKey={getRowKey}
      getDataSource={getDataSource}
      getHasMore={getHasMore}
      params={getParams}
      renderForm={renderForm}
      tableExtra={tableExtra}
    />
  )
})
